import { Controller } from '@hotwired/stimulus'
import { readCookie, createCookie } from '../utils/cookies'
import { getItem, setItem } from '../utils/local-storage'

/**
 * Контроллер управления цветовой темой.
 *
 * Подключается на `<html>` — элемент, на котором устанавливается `data-theme`.
 * Поддерживает три режима: `light`, `dark`, `auto` (следует за настройками ОС).
 * Выбор пользователя сохраняется в cookie и восстанавливается при загрузке.
 * В режиме `auto` контроллер слушает `matchMedia` и обновляет тему при смене
 * системных настроек.
 *
 * @example Подключение контроллера
 * <html data-controller="themes">
 *
 * @example Свитчер (можно разместить в нескольких местах)
 * <input type="radio" name="color-theme" value="light" data-action="change->themes#toggleTheme">
 * <input type="radio" name="color-theme" value="auto"  data-action="change->themes#toggleTheme">
 * <input type="radio" name="color-theme" value="dark"  data-action="change->themes#toggleTheme">
 */
export default class ThemesController extends Controller {
  /**
   * Вызывается при подключении контроллера.
   * Загружает сохранённую тему и подписывается на изменения ОС.
   */
  connect() {
    this.handleOsChange = this.handleOsChange.bind(this)

    if (window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleOsChange)
      } else {
        this.mediaQuery.addListener(this.handleOsChange)
      }
    }

    this.loadTheme()
  }

  /**
   * Вызывается при отключении контроллера.
   * Снимает подписку на изменения ОС.
   */
  disconnect() {
    if (!this.mediaQuery) return

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleOsChange)
    } else {
      this.mediaQuery.removeListener(this.handleOsChange)
    }
  }

  /**
   * Читает сохранённый режим из cookie или `data-theme` и применяет тему.
   * `localStorage` используется только как fallback для старых сохранённых настроек.
   * По умолчанию — `auto`.
   */
  loadTheme() {
    const mode =
      this.readCookieTheme() ||
      this.normalizeMode(this.element.dataset.theme) ||
      this.readLocalStorageTheme() ||
      'auto'

    this.applyMode(mode)
  }

  /**
   * Применяет режим: сохраняет в cookie и устанавливает
   * итоговую тему на `<html>`.
   *
   * @param {'light' | 'dark' | 'auto'} mode — выбранный режим
   */
  applyMode(mode) {
    this.mode = this.normalizeMode(mode) || 'auto'
    this.saveTheme(this.mode)

    const resolved = this.mode === 'auto' ? this.resolveOsTheme() : this.mode
    this.applyResolvedTheme(resolved)
  }

  /**
   * Сохраняет выбранный режим в cookie, которую читает ранний bootstrap.
   *
   * @param {'light' | 'dark' | 'auto'} mode — выбранный режим
   */
  saveTheme(mode) {
    createCookie('theme', mode, 365)
    setItem('theme', mode)
  }

  /**
   * Читает режим из cookie.
   *
   * @returns {'light' | 'dark' | 'auto' | null}
   */
  readCookieTheme() {
    try {
      return this.normalizeMode(readCookie('theme'))
    } catch {
      return null
    }
  }

  /**
   * Читает старую настройку из `localStorage`.
   *
   * @returns {'light' | 'dark' | 'auto' | null}
   */
  readLocalStorageTheme() {
    try {
      return this.normalizeMode(getItem('theme'))
    } catch {
      return null
    }
  }

  /**
   * Проверяет допустимость режима темы.
   *
   * @param {string | null | undefined} mode — режим темы
   * @returns {'light' | 'dark' | 'auto' | null}
   */
  normalizeMode(mode) {
    return ['light', 'dark', 'auto'].includes(mode) ? mode : null
  }

  /**
   * Определяет тему по настройкам ОС.
   *
   * @returns {'light' | 'dark'}
   */
  resolveOsTheme() {
    if (!this.mediaQuery) return 'light'

    return this.mediaQuery.matches ? 'dark' : 'light'
  }

  /**
   * Обработчик смены системной темы.
   * Обновляет `data-theme`, только если текущий режим — `auto`.
   */
  handleOsChange() {
    if (this.mode === 'auto') {
      const resolved = this.resolveOsTheme()

      this.applyResolvedTheme(resolved)
    }
  }

  /**
   * Обработчик переключения темы через radio-кнопку.
   * Привязывается через `data-action="change->themes#toggleTheme"`.
   *
   * @param {Event} event — событие `change` от `<input type="radio">`
   */
  toggleTheme(event) {
    const theme = this.readThemeFromEvent(event)

    if (!theme) return

    this.applyMode(theme)
  }

  /**
   * Переключает фактически применённую светлую/тёмную тему.
   * Используется кнопкой-мячом в hero.
   */
  toggleResolvedTheme() {
    const theme = this.element.dataset.theme === 'dark' ? 'light' : 'dark'

    this.applyMode(theme)
  }

  /**
   * Читает режим из элемента, на котором висит Stimulus action.
   *
   * @param {Event} event — событие переключателя темы
   * @returns {'light' | 'dark' | 'auto' | null}
   */
  readThemeFromEvent(event) {
    const target = event.currentTarget || event.target
    const value = target.value || target.dataset.value

    return this.normalizeMode(value)
  }

  /**
   * Синхронизирует UI-переключатели темы с текущим режимом темы.
   */
  syncControls() {
    document
      .querySelectorAll('input[type="radio"][name="color-theme"]')
      .forEach((input) => {
        input.checked = input.value === this.mode
      })

    const isDark = this.element.dataset.theme === 'dark'

    document.querySelectorAll('[data-theme-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-pressed', isDark.toString())
    })
  }

  /**
   * Устанавливает фактическую тему и сообщает остальным контроллерам,
   * что визуальная тема страницы изменилась.
   *
   * @param {'light' | 'dark'} resolved — фактически применённая тема
   */
  applyResolvedTheme(resolved) {
    const previous = document.documentElement.dataset.theme

    document.documentElement.setAttribute('data-theme', resolved)
    this.syncControls()

    if (previous === resolved) return

    document.dispatchEvent(
      new CustomEvent('theme:change', {
        detail: {
          mode: this.mode,
          theme: resolved
        }
      })
    )
  }
}
